import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash';

import type { AccessibleOU, FuelUserAccess } from '../../models/FuelUserAccess';
import { Permissions } from '../../models/Permissions';
import type { Stage } from '../../models/Stage';
import { Fetched, NotStarted, OngoingFetch } from '../../models/Stage';
import type { IDSUser } from '../../models/user';
import { FuelPortalService } from '../../services/fuel-portal/fuel-portal-service';
import { LocalStorageService } from '../../services/local-storage';
import { NO_ACCESSIBLE_OUS_ERROR } from '../../utils/userAccessUtils';
import type { RootState } from '.';

export type FuelUserAccessState = {
  userAccessLoadingLoading: boolean;
  stage: Stage;
  hasErrors: boolean;
  permissions: Permissions;
  fuelUserAccessData: FuelUserAccess | any;
  accessibleOUs: AccessibleOU[];
};

export const initialState: FuelUserAccessState = {
  userAccessLoadingLoading: false,
  stage: new NotStarted(),
  hasErrors: false,
  permissions: null,
  fuelUserAccessData: null,
  accessibleOUs: [],
};

const fuelUserAccessSlice = createSlice({
  name: 'fuelUserAccess',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.userAccessLoadingLoading = true;
    },
    saveStage: (state, action: any) => {
      state.stage = action.payload;
    },
    savePermissions: (state, action: any) => {
      state.permissions = action.payload;
      state.userAccessLoadingLoading = false;
    },
    saveFuelUserAccessData: (state, action: any) => {
      state.fuelUserAccessData = action.payload;
      state.accessibleOUs = state.fuelUserAccessData.accessibleOUs;
      state.userAccessLoadingLoading = false;
    },
  },
  extraReducers: {},
});

export const { saveStage, startLoading, savePermissions, saveFuelUserAccessData } = fuelUserAccessSlice.actions;

// export fuelUserAccessSlice selector to get the slice in any component
export const fuelUserAccessSliceSelector = (state: RootState) => state.fuelUserAccess;
// export The reducer
const fuelUserAccessReducer = fuelUserAccessSlice.reducer;
export default fuelUserAccessReducer;

export const fetchFuelUserAccess = createAsyncThunk(
  // action type string
  'fuelUserAccess/fetchFuelUserAccess',
  // callback function
  async (user: IDSUser, { getState, dispatch }) => {
    dispatch(startLoading());
    const state = getState() as RootState;
    const { stage } = state.fuelUserAccess;
    const executeNewFetch: (key: string) => Promise<Permissions> = (key) => {
      // console.log('key', key)
      const userAccessPromise = FuelPortalService.getUserAccess().then((userAccess: FuelUserAccess) => {
        // console.log('getting user access', userAccess);
        if (!userAccess?.accessibleOUs?.length) {
          throw new Error(NO_ACCESSIBLE_OUS_ERROR);
        }
        if (userAccess) {
          let loggedInOUId: string = null;
          const defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');

          if (defaultLoginOu) {
            const index = _.findIndex(userAccess.accessibleOUs, (a) => a.ouId === defaultLoginOu);
            if (index > -1) {
              loggedInOUId = defaultLoginOu;
            }
          }
          if (!loggedInOUId && userAccess.defaultLoginOu) {
            const index = _.findIndex(userAccess.accessibleOUs, (a) => a.ouId === userAccess.defaultLoginOu);
            if (index > -1 && userAccess.defaultLoginOu) {
              loggedInOUId = userAccess.defaultLoginOu;
              // console.log('setting defaultLoginOu to', loggedInOUId);
              LocalStorageService.setItem('defaultLoginOu', loggedInOUId);
            }
          }
          if (!loggedInOUId && userAccess.accessibleOUs && userAccess.accessibleOUs.length > 0 && userAccess.accessibleOUs[0].ouId) {
            loggedInOUId = userAccess.accessibleOUs[0].ouId;
            LocalStorageService.setItem('defaultLoginOu', loggedInOUId);
          }
          dispatch(saveFuelUserAccessData(userAccess));
          if (loggedInOUId) {
            const permissionsFetchPromise = FuelPortalService.getUserPermissions(loggedInOUId).then((userPermissions) => {
              let permissions = new Permissions([]);
              if (userPermissions && userPermissions.permissions && userPermissions.permissions.length > 0) {
                permissions = new Permissions(userPermissions.permissions);
              }

              dispatch(saveStage(new Fetched(key, permissions)));
              dispatch(savePermissions(permissions));

              return permissions;
            });
            return permissionsFetchPromise;
          }
        }
        return userAccessPromise;
      });
      dispatch(saveStage(new OngoingFetch(key, userAccessPromise)));
      // dispatch(savePermissions(userAccessPromise));
      return userAccessPromise;
    };
    const generateKey = () => {
      const { id_token, access_token, expires_at, sub }: IDSUser = user;
      return [id_token, access_token, expires_at.toString(), sub].join('|');
    };
    try {
      // console.log('stage',stage instanceof NotStarted);
      if (stage instanceof NotStarted) {
        const key = generateKey();
        return await executeNewFetch(key);
      }
      if (stage instanceof Fetched) {
        const newKey = generateKey();
        const existingKey = stage.key;
        return newKey === existingKey ? dispatch(savePermissions(stage.permissions)) : await executeNewFetch(newKey);
      }
      throw new Error(`Unexpected stage!: ${stage}`);
    } catch (error) {
      console.error('[useraccess] fetchFuelUserAccess failed.', error);
      throw error;
    }
  }
);
